import * as React from "react";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type { Column, ColumnDef, VisibilityState } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown, Settings2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Skeleton,
  cn,
} from "@erp/ui";

import type { DataTableColumnMeta, SortState } from "./types";
import type { CursorQueryResult } from "./use-cursor-query";

const SKELETON_ROW_COUNT = 5;

export interface DataTableBaseProps<T> {
  /** Column defs; sort key = column id; use meta { align, numeric } for layout. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- accept ColumnDef of any cell value type
  columns: ColumnDef<T, any>[];
  /** Current server sort (controlled). */
  sort?: SortState;
  /**
   * Server sort callback — clicking a sortable header cycles asc → desc → asc
   * for that column id. Omit to disable sorting UI entirely.
   */
  onSortChange?: (sort: SortState) => void;
  onRowClick?: (row: T) => void;
  /** Stable row identity (server id, never the array index). */
  getRowId: (row: T) => string;
  /** Override for the empty state; defaults to t("table.empty"). */
  emptyMessage?: string;
  /** Override for the error state; defaults to t("table.error"). */
  errorMessage?: string;
  /**
   * localStorage key persisting column visibility across visits. Omit to keep
   * visibility in-memory only.
   */
  storageKey?: string;
  /** Set false to hide the Columns visibility dropdown. Defaults to true. */
  showColumnsMenu?: boolean;
  /** Class for the outer wrapper. */
  className?: string;
  /**
   * Class for the scroll container around the table — constrain its height
   * (e.g. "max-h-[32rem]") to activate the sticky header.
   */
  containerClassName?: string;
}

export type DataTableProps<T> =
  | (DataTableBaseProps<T> & { rows: T[]; query?: never })
  | (DataTableBaseProps<T> & { query: CursorQueryResult<T>; rows?: never });

function readStoredVisibility(storageKey: string | undefined): VisibilityState {
  if (!storageKey || typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const visibility: VisibilityState = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "boolean") visibility[key] = value;
    }
    return visibility;
  } catch {
    return {};
  }
}

function metaOf<T>(column: Column<T, unknown>): DataTableColumnMeta {
  return column.columnDef.meta ?? {};
}

function alignmentClass(meta: DataTableColumnMeta): string {
  const align = meta.align ?? (meta.numeric ? "end" : "start");
  return align === "end" ? "text-end" : "text-start";
}

/** Human label for the Columns menu: string header if there is one, else the id. */
function columnLabel<T>(column: Column<T, unknown>): string {
  const header = column.columnDef.header;
  return typeof header === "string" ? header : column.id;
}

/**
 * Server-driven data table. No client pagination or sorting: sorting is a
 * controlled `sort`/`onSortChange` pair sent to the server, pagination is a
 * cursor query (`query` from useCursorQuery) with a load-more footer that also
 * auto-loads via an IntersectionObserver sentinel. Static `rows` mode is for
 * small already-loaded collections.
 */
export function DataTable<T>(props: DataTableProps<T>): React.ReactElement {
  const {
    columns,
    sort = null,
    onSortChange,
    onRowClick,
    getRowId,
    emptyMessage,
    errorMessage,
    storageKey,
    showColumnsMenu = true,
    className,
    containerClassName,
  } = props;
  const staticRows = props.rows;
  const query = props.query;
  const { t } = useTranslation();

  const data: T[] = staticRows ?? query?.rows ?? [];

  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() =>
    readStoredVisibility(storageKey),
  );

  React.useEffect(() => {
    if (!storageKey || typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnVisibility));
    } catch {
      // Storage may be full or blocked; visibility just won't persist.
    }
  }, [storageKey, columnVisibility]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => getRowId(row),
    manualSorting: true,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
  });

  const visibleColumnCount = Math.max(table.getVisibleLeafColumns().length, 1);
  const isLoading = query !== undefined && query.isLoading;
  const isErrorState = query !== undefined && query.isError && data.length === 0;
  const isEmpty = !isLoading && !isErrorState && data.length === 0;
  const hasNextPage = query?.hasNextPage ?? false;

  // Latest-callback ref so the IntersectionObserver never captures stale state.
  const loadMoreRef = React.useRef<() => void>(() => undefined);
  React.useEffect(() => {
    loadMoreRef.current = () => {
      if (query && query.hasNextPage && !query.isFetchingNextPage) {
        query.fetchNextPage();
      }
    };
  });

  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMoreRef.current();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage]);

  const cycleSort = (key: string) => {
    if (!onSortChange) return;
    const next: SortState =
      sort !== null && sort.key === key && sort.dir === "asc"
        ? { key, dir: "desc" }
        : { key, dir: "asc" };
    onSortChange(next);
  };

  const hideableColumns = table.getAllLeafColumns().filter((column) => column.getCanHide());

  return (
    <div className={cn("w-full", className)}>
      {showColumnsMenu && hideableColumns.length > 0 ? (
        <div className="mb-2 flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 aria-hidden="true" />
                {t("table.columns")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {hideableColumns.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={column.getIsVisible()}
                  onCheckedChange={(checked) => column.toggleVisibility(checked === true)}
                  // Keep the menu open so several columns can be toggled in one visit.
                  onSelect={(event) => event.preventDefault()}
                >
                  {columnLabel(column)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}

      <div className={cn("relative w-full overflow-auto rounded-md border", containerClassName)}>
        <table className="w-full caption-bottom text-sm">
          <thead className="sticky top-0 z-10 bg-background">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => {
                  const meta = metaOf(header.column);
                  const sortable = onSortChange !== undefined && header.column.getCanSort();
                  const activeDir =
                    sort !== null && sort.key === header.column.id ? sort.dir : null;
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      scope="col"
                      aria-sort={
                        activeDir === null
                          ? undefined
                          : activeDir === "asc"
                            ? "ascending"
                            : "descending"
                      }
                      className={cn(
                        "h-10 whitespace-nowrap px-2 align-middle text-sm font-medium text-muted-foreground",
                        alignmentClass(meta),
                      )}
                    >
                      {header.isPlaceholder ? null : sortable ? (
                        <button
                          type="button"
                          onClick={() => cycleSort(header.column.id)}
                          className={cn(
                            "inline-flex items-center gap-1 rounded-sm hover:text-foreground",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            activeDir !== null && "text-foreground",
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {activeDir === "asc" ? (
                            <ArrowUp className="size-3.5" aria-hidden="true" />
                          ) : activeDir === "desc" ? (
                            <ArrowDown className="size-3.5" aria-hidden="true" />
                          ) : (
                            <ChevronsUpDown className="size-3.5 opacity-50" aria-hidden="true" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`} className="border-b">
                  {table.getVisibleLeafColumns().map((column) => (
                    <td key={column.id} className="p-2 align-middle">
                      <Skeleton className="h-4 w-full max-w-32" />
                    </td>
                  ))}
                </tr>
              ))
            ) : isErrorState ? (
              <tr>
                <td colSpan={visibleColumnCount} className="p-8">
                  <div role="alert" className="flex flex-col items-center gap-3 text-center">
                    <p className="text-sm text-muted-foreground">
                      {errorMessage ?? t("table.error")}
                    </p>
                    <Button variant="outline" size="sm" onClick={() => query?.refetch()}>
                      {t("actions.retry")}
                    </Button>
                  </div>
                </td>
              </tr>
            ) : isEmpty ? (
              <tr>
                <td
                  colSpan={visibleColumnCount}
                  className="p-8 text-center text-sm text-muted-foreground"
                >
                  {emptyMessage ?? t("table.empty")}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.target !== event.currentTarget) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onRowClick(row.original);
                          }
                        }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                  className={cn(
                    "border-b transition-colors hover:bg-muted/50",
                    onRowClick &&
                      "cursor-pointer focus-visible:bg-muted/50 focus-visible:outline-none",
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = metaOf(cell.column);
                    return (
                      <td key={cell.id} className={cn("p-2 align-middle", alignmentClass(meta))}>
                        {meta.numeric ? (
                          <span
                            // physical-ok: numerals render LTR even in RTL text
                            dir="ltr"
                            className="tnum tabular-nums"
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </span>
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {hasNextPage && query !== undefined ? (
        <>
          <div ref={sentinelRef} aria-hidden="true" className="h-px" />
          <div className="flex justify-center py-2">
            <Button
              variant="outline"
              size="sm"
              loading={query.isFetchingNextPage}
              onClick={() => query.fetchNextPage()}
            >
              {t("table.loadMore")}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
